/* ==========================================================================
   No&Low Journal
   Small, deferred, dependency free. Three jobs:
     - give the body's H2s ids and build the contents list from them
     - wrap content tables so they scroll sideways on a phone
     - handle the single email signup

   The signup posts to the same Klaviyo client endpoint, company id and list
   id the Journal has always used. Nothing about that changed, it just moved
   out of the section and into this file.
   ========================================================================== */

(function () {
  'use strict';

  var KLAVIYO_COMPANY_ID = 'VmMTjk';
  var KLAVIYO_LIST_ID = 'VULVCV';

  /** Turn heading text into an anchor-safe id. */
  function slugify(text) {
    return String(text || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  }

  /* ---------------------------------------------------------------------
     Contents
     --------------------------------------------------------------------- */

  function buildContents() {
    var prose = document.querySelector('[data-jr-prose]');
    if (!prose) return;

    // Every H2 gets an id whether or not a contents list is on the page, so
    // in-article anchor links keep working either way.
    var headings = prose.querySelectorAll('h2');
    Array.prototype.forEach.call(headings, function (heading, i) {
      if (!heading.id) {
        heading.id = slugify(heading.textContent) || 'section-' + (i + 1);
      }
    });

    var toc = document.querySelector('[data-jr-toc]');
    if (!toc) return;

    var list = toc.querySelector('[data-jr-toc-list]');
    if (!list || !headings.length) {
      // A contents box with nothing to list is just an empty box.
      toc.hidden = true;
      return;
    }

    Array.prototype.forEach.call(headings, function (heading) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + heading.id;
      a.textContent = heading.textContent.trim();
      li.appendChild(a);
      list.appendChild(li);
    });

    // Open on desktop by default, collapsed on a phone where it would push
    // the article too far down. The desktop behaviour is a block setting,
    // and either way the reader can toggle it themselves afterwards.
    var openOnDesktop = toc.dataset.jrTocOpenDesktop !== 'false';
    if (openOnDesktop && window.matchMedia && window.matchMedia('(min-width: 768px)').matches) {
      toc.open = true;
    }
  }

  /* ---------------------------------------------------------------------
     Tables
     --------------------------------------------------------------------- */

  function wrapTables() {
    var prose = document.querySelector('[data-jr-prose]');
    if (!prose) return;

    var tables = prose.querySelectorAll('table');
    Array.prototype.forEach.call(tables, function (table) {
      if (table.parentNode && table.parentNode.classList.contains('jr-table-scroll')) return;

      var wrap = document.createElement('div');
      wrap.className = 'jr-table-scroll';
      // tabindex makes the scroll region reachable by keyboard, which is a
      // WCAG requirement for anything that scrolls.
      wrap.setAttribute('tabindex', '0');
      wrap.setAttribute('role', 'region');

      var caption = table.querySelector('caption');
      wrap.setAttribute('aria-label', caption ? caption.textContent.trim() : 'Comparison table');

      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
    });
  }

  /* ---------------------------------------------------------------------
     Email signup
     --------------------------------------------------------------------- */

  /**
   * Subscribe an address to the Journal list.
   * Kept on window as nlSubscribe for backwards compatibility with any
   * markup elsewhere that still calls it inline.
   */
  function nlSubscribe(event, form) {
    if (event) event.preventDefault();
    if (!form) return false;

    var input = form.querySelector('input[type=email]');
    var button = form.querySelector('button[type=submit]');
    var status = form.parentNode ? form.parentNode.querySelector('[data-jr-status]') : null;
    if (!input) return false;

    var email = input.value.trim();
    if (!email) return false;

    var idleLabel = button ? button.textContent : '';
    if (button) {
      button.disabled = true;
      button.textContent = form.getAttribute('data-busy-label') || 'Signing up...';
    }
    if (status) status.textContent = '';

    fetch('https://a.klaviyo.com/client/subscriptions?company_id=' + KLAVIYO_COMPANY_ID, {
  method: 'POST',
  headers: { 'content-type': 'application/vnd.api+json', revision: '2024-10-15' },
  body: JSON.stringify({
    data: {
      type: 'subscription',
      attributes: {
        custom_source: form.getAttribute('data-source') || 'Journal',
        profile: {
          data: {
            type: 'profile',
            attributes: { email: email, properties: { signup_page: location.pathname } },
          },
        },
      },
      relationships: {
        list: { data: { type: 'list', id: KLAVIYO_LIST_ID } },
      },
    },
  }),
})
      .then(function (response) {
        if (response.ok || response.status === 202) {
          if (status) {
            status.textContent = form.getAttribute('data-success') || 'You are in. Check your inbox.';
          }
          form.hidden = true;
        } else {
          throw new Error('Subscription failed');
        }
      })
      .catch(function () {
        if (status) {
          status.textContent =
            form.getAttribute('data-error') || 'Sorry, that did not go through. Please try again.';
        }
        if (button) {
          button.disabled = false;
          button.textContent = idleLabel;
        }
      });

    return false;
  }

  window.nlSubscribe = nlSubscribe;

  function bindSignups() {
    var forms = document.querySelectorAll('[data-jr-signup-form]');
    Array.prototype.forEach.call(forms, function (form) {
      if (form.dataset.jrBound === '1') return;
      form.dataset.jrBound = '1';
      form.addEventListener('submit', function (event) {
        nlSubscribe(event, form);
      });
    });
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */

  function boot() {
    buildContents();
    wrapTables();
    bindSignups();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-run when a block is re-rendered in the theme editor.
  document.addEventListener('shopify:block:load', bindSignups);
  document.addEventListener('shopify:section:load', boot);
})();
